import { QuoteLineItem } from '../types';

interface QuoteItemsTableProps {
  title: string;
  items: QuoteLineItem[];
  included: boolean;
  hideMonthlyAnnual?: boolean;
  onDemandTotal?: number;
  projectMgmtTotal?: number;
}

const QuoteItemsTable = ({ title, items, included, hideMonthlyAnnual = false, onDemandTotal, projectMgmtTotal }: QuoteItemsTableProps) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (!included || items.length === 0) {
    return null;
  }

  const showOverheadRows = hideMonthlyAnnual && (
    (onDemandTotal !== undefined && onDemandTotal > 0) ||
    (projectMgmtTotal !== undefined && projectMgmtTotal > 0)
  );

  const implementationSubtotal = items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
  const totalImplementation = implementationSubtotal + (onDemandTotal || 0) + (projectMgmtTotal || 0);

  return (
    <div className="bg-card rounded-lg border border-border p-6 mb-6">
      <h2 className="text-lg font-semibold text-text-primary mb-4">{title}</h2>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">
                Product
              </th>
              <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">
                Unit Price
              </th>
              <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">
                Quantity
              </th>
              {hideMonthlyAnnual ? (
                <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">
                  Total
                </th>
              ) : (
                <>
                  <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">
                    Monthly
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">
                    Annual
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-border last:border-0">
                <td className="py-3 px-4 text-sm text-text-primary font-medium">
                  {item.productName}
                </td>
                <td className="py-3 px-4 text-sm text-text-primary text-right font-mono">
                  {formatCurrency(item.unitPrice)}
                </td>
                <td className="py-3 px-4 text-sm text-text-primary text-right">
                  {item.quantity}
                </td>
                {hideMonthlyAnnual ? (
                  <td className="py-3 px-4 text-sm text-text-primary text-right font-mono">
                    {formatCurrency(item.unitPrice * item.quantity)}
                  </td>
                ) : (
                  <>
                    <td className="py-3 px-4 text-sm text-text-primary text-right font-mono">
                      {formatCurrency(item.monthlyAmount)}
                    </td>
                    <td className="py-3 px-4 text-sm text-text-primary text-right font-mono">
                      {formatCurrency(item.annualAmount)}
                    </td>
                  </>
                )}
              </tr>
            ))}

            {showOverheadRows && (
              <>
                {(onDemandTotal !== undefined && onDemandTotal > 0) && (
                  <tr className="border-b border-border bg-muted/30">
                    <td className="py-3 px-4 text-sm text-text-secondary italic" colSpan={3}>
                      On demand services (estimate for meetings, quality assurance, ...)
                    </td>
                    <td className="py-3 px-4 text-sm text-text-primary text-right font-mono">
                      {formatCurrency(onDemandTotal)}
                    </td>
                  </tr>
                )}
                {(projectMgmtTotal !== undefined && projectMgmtTotal > 0) && (
                  <tr className="border-b border-border bg-muted/30">
                    <td className="py-3 px-4 text-sm text-text-secondary italic" colSpan={3}>
                      Project Overhead 15%
                    </td>
                    <td className="py-3 px-4 text-sm text-text-primary text-right font-mono">
                      {formatCurrency(projectMgmtTotal)}
                    </td>
                  </tr>
                )}
                <tr className="bg-primary/5 border-t-2 border-primary/20">
                  <td className="py-3 px-4 text-sm font-bold text-text-primary" colSpan={3}>
                    Total Implementation Services
                  </td>
                  <td className="py-3 px-4 text-sm font-bold text-primary text-right font-mono">
                    {formatCurrency(totalImplementation)}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QuoteItemsTable;